import { AssumeRoleCommand, STSClient } from '@aws-sdk/client-sts'
import {
    CognitoIdentityCredentialProvider,
    fromCognitoIdentityPool,
} from '@aws-sdk/credential-providers'
import { CognitoAuthSession } from 'amazon-cognito-auth-js'

import {
    APP_CONFIG,
    CONFIGS_BY_ENVIRONMENT,
    QA_AUTOMATION_STS_ASSUME_ROLE,
    STAGE,
} from 'src/config.app'
import { WbsAuthenticator } from 'src/pages/scoring-testing-v2/authentication/WbsAuthenticator'
import { Authenticator } from 'src/services/Authenticator'
import { ClientConfiguration } from 'src/services/aws/DDBHandler'
import { retryAuth } from 'src/services/AxiosInterceptor'

export const REGION = 'us-west-2'

export const getCredentials = async (): Promise<CognitoIdentityCredentialProvider> => {
    const userSession = await retryAuth(() => Authenticator.getValidUserSession())

    const userJwtToken: string = userSession.getIdToken().getJwtToken()
    const cognitoLoginURL = `cognito-idp.${REGION}.amazonaws.com/${
        APP_CONFIG.auth.UserPoolId || ''
    }`

    return fromCognitoIdentityPool({
        identityPoolId: APP_CONFIG.identityPoolId,
        logins: { [cognitoLoginURL]: userJwtToken },
        clientConfig: {
            // https://github.com/aws/aws-sdk-js-v3/issues/2958
            region: REGION,
        },
    })
}

export const getWbsCredentials = async (
    stage?: STAGE
): Promise<CognitoIdentityCredentialProvider> => {
    const userSession: CognitoAuthSession = await retryAuth(() =>
        WbsAuthenticator.getValidUserSession()
    )

    const userJwtToken: string = userSession.getIdToken().getJwtToken()
    const cognitoLoginURL = `cognito-idp.${REGION}.amazonaws.com/${
        stage
            ? CONFIGS_BY_ENVIRONMENT[stage].wbsAuth.UserPoolId || ''
            : APP_CONFIG.wbsAuth.UserPoolId || ''
    }`

    return fromCognitoIdentityPool({
        identityPoolId: stage
            ? CONFIGS_BY_ENVIRONMENT[stage].wbsConfig.wbsIdentityPoolId
            : APP_CONFIG.wbsConfig.wbsIdentityPoolId,
        logins: {
            [cognitoLoginURL]: userJwtToken,
        },
        clientConfig: {
            // https://github.com/aws/aws-sdk-js-v3/issues/2958
            region: REGION,
        },
    })
}

export const setCrossAccountCredentials = async (): Promise<ClientConfiguration> => {
    const sts = new STSClient({
        region: REGION,
        credentials: await getCredentials(),
    })

    const timestamp = new Date().getTime()
    const params = {
        RoleArn: QA_AUTOMATION_STS_ASSUME_ROLE,
        RoleSessionName: `pulling-test-results-${timestamp}`,
    }

    const data = await sts.send(new AssumeRoleCommand(params))
    if (
        !data.Credentials ||
        !data.Credentials.AccessKeyId ||
        !data.Credentials.SecretAccessKey ||
        !data.Credentials.SessionToken
    ) {
        throw new Error('setCrossAccountCredentials: Failed to assume role')
    }

    return {
        accessKeyId: data.Credentials.AccessKeyId,
        secretAccessKey: data.Credentials.SecretAccessKey,
        sessionToken: data.Credentials.SessionToken,
    }
}
